import React from 'react';

import WebflowUnitInput from '@packages/systems/design-system/WebflowUnitInput';
import {Ok} from '@packages/utilities/fp/result';
import {isValidNumber} from '@packages/systems/core/utils/NumberUtils';

const FRAME_SIZE_UNIT_SETTINGS = {
  unit: 'PX',
  step: 1,
  min: 0,
} as const;

const DEFAULT_VALUE = 0;

type FrameSizeUnitValue = {
  unit: string;
  value: number | string | undefined;
};

const getValue = (value: number | string | undefined) => {
  let numericValue = DEFAULT_VALUE;

  if (typeof value === 'number') {
    numericValue = value;
  } else if (value != null) {
    const parsed = parseFloat(String(value));

    if (!isNaN(parsed)) numericValue = parsed;
  }

  return {
    unit: FRAME_SIZE_UNIT_SETTINGS.unit,
    value: numericValue,
  };
};

const parseIntegerFromValue = (value: FrameSizeUnitValue) => {
  const parsed =
    typeof value.value === 'number'
      ? value.value
      : parseFloat(String(value.value ?? ''));

  const floatVal = Number.isNaN(parsed) ? DEFAULT_VALUE : parsed;
  return Math.round(floatVal);
};

const getUnit = () => FRAME_SIZE_UNIT_SETTINGS.unit;

const isValidInputValue = (_: string, input: string) => isValidNumber(input);

const PLACEHOLDER = {
  unit: FRAME_SIZE_UNIT_SETTINGS.unit,
  value: DEFAULT_VALUE,
} as const;

/**
 * A pixel-only numeric input for frame dimensions (width/height).
 * Wraps WebflowUnitInput with frame-specific parsing:
 * strips "px" suffix, rounds to integers, clamps to min/max range.
 */
export const FrameSizeUnitInput: React.FC<{
  automationId: string;
  inputValue: number;
  min: number;
  max: number;
  onInputChange?: (value: number) => void;
  dimmed?: boolean;
  disabled?: boolean;
  'aria-label'?: string;
  'aria-describedby'?: string;
}> = ({
  automationId,
  inputValue,
  min,
  max,
  onInputChange,
  dimmed,
  disabled,
  'aria-label': ariaLabel,
  'aria-describedby': ariaDescribedBy,
}) => {
  /**
   * Attempts to parse the provided value as a number. If it fails (for example for non-numeric input), falls back to the current input value.
   */
  const parseValue = React.useCallback(
    (_: string, rawValue: string) => {
      const sanitized = rawValue.replace(/px/gi, '');
      const parsed = parseFloat(sanitized);

      if (Number.isNaN(parsed)) {
        return Ok({
          unit: FRAME_SIZE_UNIT_SETTINGS.unit,
          value: inputValue,
        });
      }

      return Ok({
        unit: FRAME_SIZE_UNIT_SETTINGS.unit,
        value: parsed,
      });
    },
    [inputValue]
  );

  const units = React.useMemo(
    () => [
      {
        unit: FRAME_SIZE_UNIT_SETTINGS.unit,
        range: {
          min,
          max,
          step: FRAME_SIZE_UNIT_SETTINGS.step,
          default: DEFAULT_VALUE,
        },
        menuName: FRAME_SIZE_UNIT_SETTINGS.unit,
      },
    ],
    [min, max]
  );

  const onChange = React.useCallback(
    (value: FrameSizeUnitValue) => {
      const number = parseIntegerFromValue(value);
      onInputChange?.(number);
    },
    [onInputChange]
  );

  // Round the input value to match what's displayed in the input.
  // This prevents blur from triggering onChange when the displayed (rounded)
  // value differs from the original sub-pixel measured value.
  const roundedInputValue = Math.round(inputValue);

  return (
    <div
      style={{
        flex: 2,
        cursor: disabled ? 'not-allowed' : undefined,
      }}
    >
      <WebflowUnitInput
        style={{
          ...(disabled ? {pointerEvents: 'none' as const} : {}),
          // @ts-expect-error the type doesn't expect variables
          '--colors-text-primary': dimmed
            ? 'var(--colors-text-dimmed)'
            : undefined,
        }}
        data-automation-id={automationId}
        onChange={onChange}
        isValidInputValue={isValidInputValue}
        getValue={getValue}
        getUnit={getUnit}
        units={units}
        value={{unit: FRAME_SIZE_UNIT_SETTINGS.unit, value: roundedInputValue}}
        parseValue={parseValue}
        getNumberFromValue={parseIntegerFromValue}
        placeholder={PLACEHOLDER}
        disabled={disabled}
        aria-label={ariaLabel}
        aria-describedby={ariaDescribedBy}
      />
    </div>
  );
};
